/**
 * The owner's side of "How it was built": import chat histories from other
 * coding agents, and choose what a share link shows — the build sessions,
 * the code, or both.
 *
 * Lives in the Share dialog below the public links, because it answers the
 * same question ("what can someone with the link see?"). Both switches are
 * admin-only, exactly like minting a link; importing is open to anyone who
 * can edit the project. Every request's route is documented in
 * `docs/transcript-import-and-public-workspace-2026-09-29.md`.
 *
 * @module
 */

import { useCallback, useEffect, useRef, useState } from 'react'

import { useHttpClient, useTranslation } from '@molecule/app-react'
import { getClassMap } from '@molecule/app-ui'
import { Button } from '@molecule/app-ui-react'

import {
  errorKeyOf,
  IMPORT_ACCEPT,
  MAX_IMPORT_BYTES,
  pick,
  type PublicSession,
  statusOf,
  type TranscriptSummary,
} from '../lib/public-build.js'
import { SessionReader } from '../pages/share/BuildSessions.js'

/** Authored server refusal keys, translated just like the sandbox error copy. */
const CAPTURE_ERROR_COPY: Readonly<Record<string, string>> = {
  'workspace.capture.noRepository':
    'No Git repository was found. Open your workspace and try again.',
  'workspace.capture.gitOwnership':
    'Git cannot read this repository because its ownership does not match the workspace.',
  'workspace.capture.gitFailed':
    'Git could not list the source files. Check that the repository is readable and try again.',
  'workspace.capture.tooManyFiles': 'The repository has too many files to publish (maximum 3000).',
  'workspace.capture.fileTooLarge': 'A source file exceeds the 5 MiB publication limit: {{path}}',
  'workspace.capture.totalTooLarge': 'The source exceeds the 25 MiB publication limit.',
  'workspace.capture.sourceChanged':
    'A source file changed while preparing publication. Try again.',
  'workspace.capture.noTempSpace':
    'Not enough temporary storage for source publication. Try again later.',
  'workspace.capture.fileUnreadable':
    'A source file could not be read. Check its permissions and try again.',
  'workspace.captureFailed': 'Could not prepare source publication. Try again.',
}

/**
 * A time in the reader's locale, or the raw value when it does not parse.
 *
 * @param iso - An ISO 8601 time.
 * @returns The formatted date and time.
 */
export function formatWhen(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
}

/** One file's import outcome, as shown in the results list. */
export interface ImportRow {
  name: string
  status: 'reading' | 'imported' | 'duplicate' | 'unrecognized' | 'too-large' | 'failed'
  summary?: TranscriptSummary
}

/**
 * Imports one file: reads it as text and POSTs it.
 *
 * @param file - The chosen file.
 * @param post - Sends the import request; resolves to the HTTP status and body.
 * @returns The row describing the outcome.
 */
export async function importOne(
  file: File,
  post: (body: { fileName: string; text: string }) => Promise<{ status: number; data: unknown }>,
): Promise<ImportRow> {
  if (file.size > MAX_IMPORT_BYTES) return { name: file.name, status: 'too-large' }
  let text: string
  try {
    text = await file.text()
  } catch (_error) {
    // The browser could not read the file (removed, permission lost).
    return { name: file.name, status: 'failed' }
  }
  try {
    const res = await post({ fileName: file.name, text })
    const summary = pick<TranscriptSummary>(res.data, 'transcript')
    return { name: file.name, status: res.status === 200 ? 'duplicate' : 'imported', summary }
  } catch (error) {
    const status = statusOf(error)
    if (status === 422 || errorKeyOf(error) === 'transcript.unrecognized')
      return { name: file.name, status: 'unrecognized' }
    if (status === 413) return { name: file.name, status: 'too-large' }
    return { name: file.name, status: 'failed' }
  }
}

/** Files and omissions in the exact source snapshot awaiting owner confirmation. */
interface PublicationDraft {
  publicationId: string
  revision: string
  files: { path: string; size: number; binary: boolean }[]
  excluded: { path: string; reason: string }[]
  totalBytes: number
}

interface PublicationPreparation {
  status: 'preparing'
  operationId: string
  retryAfter?: number
}

/** Publication controls shared by every owner Share entry point. */
export function PublicBuildPanel({
  projectId,
  canAdmin,
  canEdit,
}: {
  projectId: string
  canAdmin: boolean
  canEdit: boolean
}) {
  const cm = getClassMap()
  const { t } = useTranslation()
  const http = useHttpClient()
  const httpRef = useRef(http)
  httpRef.current = http
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [sessions, setSessions] = useState<TranscriptSummary[]>([])
  const [selected, setSelected] = useState<string[]>([])
  const [defaultId, setDefaultId] = useState<string | null>(null)
  const [rows, setRows] = useState<ImportRow[]>([])
  const [loaded, setLoaded] = useState(false)
  const [published, setPublished] = useState(false)
  const [capturedAt, setCapturedAt] = useState<string | null>(null)
  const [draft, setDraft] = useState<PublicationDraft | null>(null)
  const [acknowledged, setAcknowledged] = useState(false)
  const [busy, setBusy] = useState<'preview' | 'publish' | 'unpublish' | 'conversations' | null>(
    null,
  )
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [reviewFile, setReviewFile] = useState<string | null>(null)
  const [reviewText, setReviewText] = useState<string | null>(null)
  const [reviewError, setReviewError] = useState(false)
  const [dragging, setDragging] = useState(false)
  const [reviewConversationId, setReviewConversationId] = useState<string | null>(null)
  const [conversationReview, setConversationReview] = useState<PublicSession | null>(null)
  const [conversationReviewError, setConversationReviewError] = useState(false)

  const load = useCallback(async () => {
    try {
      const [list, project] = await Promise.all([
        httpRef.current.get<unknown>(`/projects/${projectId}/transcripts`),
        httpRef.current.get<unknown>(`/projects/${projectId}`),
      ])
      const items = pick<TranscriptSummary[]>(list.data, 'transcripts') ?? []
      setSessions(items)
      setSelected(items.filter((s) => s.public === true).map((s) => s.id))
      setDefaultId(pick<string | null>(list.data, 'defaultConversationId') ?? null)
      const settings =
        pick<Record<string, unknown>>(project.data, 'settings') ??
        pick<Record<string, unknown>>(pick(project.data, 'props'), 'settings') ??
        {}
      const repo = pick<{ publicationId?: string; capturedAt?: string } | null>(
        project.data,
        'repository',
      )
      setPublished(
        Boolean(repo?.publicationId) ||
          (settings.publicWorkspace === true &&
            typeof settings.publicWorkspacePublicationId === 'string'),
      )
      setCapturedAt(
        repo?.capturedAt ??
          (typeof settings.publicWorkspaceCapturedAt === 'string'
            ? settings.publicWorkspaceCapturedAt
            : null),
      )
      setLoaded(true)
    } catch (_error) {
      // The failure is surfaced in this control’s visible error state.
      setError(
        t('publicBuild.loadError', undefined, {
          defaultValue: 'Could not load sharing settings. Try again.',
        }),
      )
    }
  }, [projectId, t])
  // Keep translations out of the fetch dependency: some providers return a new translator each render.
  const loadRef = useRef(load)
  loadRef.current = load
  useEffect(() => {
    void loadRef.current()
  }, [projectId])

  useEffect(() => {
    if (!draft || !reviewFile) {
      setReviewText(null)
      return
    }
    let cancelled = false
    setReviewText(null)
    setReviewError(false)
    if (draft.files.find((f) => f.path === reviewFile)?.binary) return
    void httpRef.current
      .get<string>(
        `/projects/${projectId}/public-workspace/preview/${encodeURIComponent(draft.publicationId)}/files/raw?path=${encodeURIComponent(reviewFile)}`,
        { responseType: 'text' },
      )
      .then((response) => {
        if (!cancelled) setReviewText(response.data)
      })
      .catch(() => {
        if (!cancelled) setReviewError(true)
      })
    return () => {
      cancelled = true
    }
  }, [draft, reviewFile, projectId])

  useEffect(() => {
    setConversationReview(null)
    setConversationReviewError(false)
    if (!reviewConversationId) return
    let cancelled = false
    void httpRef.current
      .get<unknown>(
        `/projects/${projectId}/transcripts/${encodeURIComponent(reviewConversationId)}`,
      )
      .then((response) => {
        if (cancelled) return
        const session = pick<PublicSession>(response.data, 'session')
        if (session) setConversationReview(session)
        else setConversationReviewError(true)
      })
      .catch((_error: unknown) => {
        // Keep the requested review visible as an error; never substitute another conversation.
        if (!cancelled) setConversationReviewError(true)
      })
    return () => {
      cancelled = true
    }
  }, [projectId, reviewConversationId])

  const preview = async () => {
    setBusy('preview')
    setError(null)
    setNotice(
      t('publicBuild.preparingNotice', undefined, {
        defaultValue: 'Preparing a private source snapshot. This can take a moment.',
      }),
    )
    setAcknowledged(false)
    setDraft(null)
    setReviewFile(null)
    try {
      const path = `/projects/${projectId}/public-workspace/preview`
      const deadline = Date.now() + 10 * 60_000
      // One identity per deliberate click survives Machine routing and transport retries.
      const operationId = crypto.randomUUID()
      let response = await httpRef.current.put<PublicationDraft | PublicationPreparation>(path, {
        operationId,
        refresh: true,
      })
      while (response.status === 202) {
        if (Date.now() >= deadline) throw new Error('Repository preparation timed out')
        if (!('operationId' in response.data) || response.data.operationId !== operationId)
          throw new Error('Invalid publication preparation')
        const retryAfter =
          'retryAfter' in response.data && typeof response.data.retryAfter === 'number'
            ? response.data.retryAfter
            : 1
        await new Promise<void>((resolve) =>
          setTimeout(resolve, Math.max(0, Math.min(retryAfter, 5)) * 1000),
        )
        response = await httpRef.current.put<PublicationDraft | PublicationPreparation>(path, {
          operationId,
        })
      }
      const data = response.data
      if (!('publicationId' in data) || !data.publicationId || !Array.isArray(data.files))
        throw new Error('Invalid publication draft')
      setDraft(data)
      setNotice(null)
    } catch (cause) {
      const body = (
        cause as {
          response?: {
            data?: {
              error?: string
              errorKey?: string
              errorValues?: { path?: string }
            }
          }
        }
      )?.response?.data
      const key = body?.errorKey
      const copy = key && Object.hasOwn(CAPTURE_ERROR_COPY, key) ? CAPTURE_ERROR_COPY[key] : null
      setNotice(null)
      setError(
        (key && copy
          ? t(key, { path: body?.errorValues?.path ?? '[file]' }, { defaultValue: copy })
          : body?.error) ??
          t('publicBuild.previewError', undefined, {
            defaultValue:
              'Could not prepare the source snapshot. Open your workspace and try again.',
          }),
      )
    } finally {
      setBusy(null)
    }
  }
  const publish = async () => {
    if (!draft || !acknowledged || busy) return
    setBusy('publish')
    setError(null)
    try {
      const response = await httpRef.current.put<unknown>(
        `/projects/${projectId}/public-workspace`,
        { enabled: true, publicationId: draft.publicationId },
      )
      setPublished(true)
      setCapturedAt(pick<string>(response.data, 'capturedAt') ?? null)
      setDraft(null)
      setNotice(
        t('publicBuild.published', undefined, {
          defaultValue: 'The reviewed repository is public through your share links.',
        }),
      )
    } catch (_error) {
      // The failure is surfaced in this control’s visible error state.
      setError(
        t('publicBuild.publishError', undefined, {
          defaultValue:
            'Could not publish this snapshot. The previous publication has not been replaced.',
        }),
      )
    } finally {
      setBusy(null)
    }
  }
  const unpublish = async () => {
    setBusy('unpublish')
    setError(null)
    try {
      await httpRef.current.put(`/projects/${projectId}/public-workspace`, { enabled: false })
      setPublished(false)
      setCapturedAt(null)
      setDraft(null)
      setNotice(
        t('publicBuild.unpublished', undefined, {
          defaultValue:
            'The source repository is no longer available through your share links. Existing clones cannot be recalled.',
        }),
      )
    } catch (_error) {
      // The failure is surfaced in this control’s visible error state.
      setError(
        t('publicBuild.saveError', undefined, {
          defaultValue: 'Could not save that. Please try again.',
        }),
      )
    } finally {
      setBusy(null)
    }
  }
  const saveConversations = async () => {
    setBusy('conversations')
    setError(null)
    setNotice(null)
    try {
      await httpRef.current.put(`/projects/${projectId}/public-transcript`, {
        enabled: selected.length > 0,
        conversationIds: selected,
        defaultConversationId: selected.includes(defaultId ?? '')
          ? defaultId
          : (selected[0] ?? null),
      })
      setNotice(
        t('publicBuild.conversationsSaved', undefined, {
          defaultValue:
            'Public conversation choices saved. Unselected conversations remain private.',
        }),
      )
    } catch (_error) {
      // The failure is surfaced in this control’s visible error state.
      setError(
        t('publicBuild.saveError', undefined, {
          defaultValue: 'Could not save that. Please try again.',
        }),
      )
    } finally {
      setBusy(null)
    }
  }
  const toggleConversation = (id: string, checked: boolean) => {
    const next = checked ? [...selected, id] : selected.filter((s) => s !== id)
    setSelected(next)
    if (!next.includes(defaultId ?? '')) setDefaultId(next[0] ?? null)
    setNotice(null)
  }
  const importFiles = async (files: File[]) => {
    if (!files.length) return
    setRows(files.map((f) => ({ name: f.name, status: 'reading' })))
    const out: ImportRow[] = []
    for (const file of files) {
      out.push(
        await importOne(file, (body) =>
          httpRef.current.post<unknown>(`/projects/${projectId}/transcripts`, body),
        ),
      )
      setRows([
        ...out,
        ...files.slice(out.length).map((f) => ({ name: f.name, status: 'reading' as const })),
      ])
    }
    await loadRef.current()
  }
  const remove = async (id: string) => {
    try {
      await httpRef.current.delete(`/projects/${projectId}/transcripts/${id}`)
      setSessions((prev) => prev.filter((s) => s.id !== id))
      if (reviewConversationId === id) setReviewConversationId(null)
      const next = selected.filter((s) => s !== id)
      setSelected(next)
      if (id === defaultId) setDefaultId(next[0] ?? null)
    } catch (_error) {
      // The failure is surfaced in this control’s visible error state.
      setError(
        t('publicBuild.removeError', undefined, {
          defaultValue: 'Could not remove the imported conversation.',
        }),
      )
    }
  }
  const statusText = (row: ImportRow): string => {
    if (row.status === 'reading')
      return t('publicBuild.row.reading', undefined, { defaultValue: 'Importing…' })
    if (row.status === 'imported' || row.status === 'duplicate')
      return row.status === 'duplicate'
        ? t('publicBuild.row.duplicate', undefined, { defaultValue: 'Already imported' })
        : row.summary
          ? t(
              'publicBuild.row.imported',
              { harness: row.summary.harness, count: row.summary.turns },
              { defaultValue: '{{harness}}, {{count}} turns' },
            )
          : t('publicBuild.row.importedPlain', undefined, { defaultValue: 'Imported' })
    if (row.status === 'too-large')
      return t('publicBuild.row.tooLarge', undefined, {
        defaultValue: 'Too large. The limit is 20 MB.',
      })
    if (row.status === 'unrecognized')
      return t('publicBuild.row.unrecognized', undefined, {
        defaultValue: 'Not a chat history we recognize.',
      })
    return t('publicBuild.row.failed', undefined, { defaultValue: 'Could not import it.' })
  }

  return (
    <div data-mol-id="public-build-panel" style={{ display: 'grid', gap: 16 }}>
      {canAdmin && (
        <p
          className={cm.cn(cm.textSize('sm'), cm.fontWeight('medium'))}
          data-mol-id="public-build-publication-order"
        >
          {t('publicBuild.publicationOrder', undefined, {
            defaultValue:
              'Publish a reviewed repository below, then create a share link above. Conversations are optional.',
          })}
        </p>
      )}
      {canAdmin && (
        <section
          aria-label={t('publicBuild.repository', undefined, { defaultValue: 'Public repository' })}
        >
          <h3 className={cm.fontWeight('bold')}>
            {t('publicBuild.repository', undefined, { defaultValue: 'Public repository' })}
          </h3>
          <p className={cm.cn(cm.textMuted, cm.textSize('sm'))}>
            {t('publicBuild.boundary', undefined, {
              defaultValue:
                'Sharing source publishes a Git repository anyone with a link can read and clone. We use Git’s tracked and nonignored source files, excluding environment files, credentials and detected secrets. Private Git history is excluded; updates add reviewed public commits. Review the snapshot before publishing; secret detection cannot identify every sensitive file.',
            })}
          </p>
          {loaded && (
            <p data-mol-id="public-build-repository-status">
              {published
                ? t('publicBuild.repositoryPublic', undefined, {
                    defaultValue: 'Source repository is public.',
                  })
                : t('publicBuild.repositoryPrivate', undefined, {
                    defaultValue: 'Source repository is private.',
                  })}{' '}
              {capturedAt &&
                t(
                  'publicBuild.capturedAt',
                  { date: formatWhen(capturedAt) },
                  { defaultValue: 'Published {{date}}.' },
                )}
            </p>
          )}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button
              variant="outline"
              size="sm"
              disabled={!loaded || busy !== null}
              onClick={() => void preview()}
              data-mol-id="public-build-preview"
            >
              {busy === 'preview'
                ? t('publicBuild.preparing', undefined, { defaultValue: 'Preparing snapshot…' })
                : published
                  ? t('publicBuild.reviewUpdate', undefined, {
                      defaultValue: 'Review source update',
                    })
                  : t('publicBuild.reviewSource', undefined, {
                      defaultValue: 'Review source to publish',
                    })}
            </Button>
            {published && (
              <Button
                variant="ghost"
                size="sm"
                disabled={busy !== null}
                onClick={() => void unpublish()}
                data-mol-id="public-build-unpublish"
              >
                {t('publicBuild.unpublish', undefined, { defaultValue: 'Unpublish source' })}
              </Button>
            )}
          </div>
          <p className={cm.cn(cm.textMuted, cm.textSize('xs'))}>
            {t('publicBuild.snapshotHint', undefined, {
              defaultValue:
                'Publishing shares this reviewed snapshot, not future edits. Publish another snapshot to update the public repository; existing clones can fetch it. People can keep copies after you unpublish. Unpublishing then publishing again starts new public history.',
            })}
          </p>
          {draft && (
            <div
              className={cm.cn(cm.surface, cm.borderAll, cm.sp('p', 3))}
              style={{ borderRadius: 8, marginTop: 12 }}
              data-mol-id="public-build-draft"
            >
              <p className={cm.fontWeight('bold')}>
                {t(
                  'publicBuild.draftSummary',
                  {
                    count: draft.files.length,
                    size: (draft.totalBytes / 1024).toFixed(1),
                    revision: draft.revision.slice(0, 10),
                  },
                  { defaultValue: '{{count}} files · {{size}} KB · revision {{revision}}' },
                )}
              </p>
              <p className={cm.textMuted}>
                {t('publicBuild.draftPrivate', undefined, {
                  defaultValue:
                    'This draft is private until you confirm below. Select files to inspect the exact published contents.',
                })}
              </p>
              <ul
                style={{ maxHeight: 180, overflowY: 'auto', paddingLeft: 18 }}
                aria-label={t('publicBuild.includedFiles', undefined, {
                  defaultValue: 'Files to publish',
                })}
              >
                {draft.files.map((file) => (
                  <li key={file.path}>
                    <button
                      type="button"
                      className={cm.cn(cm.resultItem, cm.w('full'))}
                      style={{ textAlign: 'left', overflowWrap: 'anywhere' }}
                      onClick={() => setReviewFile(file.path)}
                      aria-current={reviewFile === file.path ? 'true' : undefined}
                      data-mol-id="public-build-draft-file"
                    >
                      <code>{file.path}</code>
                      <span className={cm.textMuted}>
                        {' '}
                        · {file.size} B
                        {file.binary &&
                          ` · ${t('publicBuild.binary', undefined, { defaultValue: 'binary' })}`}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
              {reviewFile && (
                <div>
                  <strong>{reviewFile}</strong>
                  {draft.files.find((f) => f.path === reviewFile)?.binary ? (
                    <p>
                      {t('publicBuild.binaryReview', undefined, {
                        defaultValue:
                          'Binary file: verify this asset in your workspace before publishing.',
                      })}
                    </p>
                  ) : reviewError ? (
                    <p className={cm.textError} role="alert">
                      {t('publicBuild.reviewError', undefined, {
                        defaultValue:
                          'Could not load this draft file. Please retry preparing the snapshot.',
                      })}
                    </p>
                  ) : reviewText === null ? (
                    <p role="status">
                      {t('common.loading', undefined, { defaultValue: 'Loading…' })}
                    </p>
                  ) : (
                    <pre
                      className={cm.surface}
                      style={{ maxHeight: 240, overflow: 'auto', fontSize: 12 }}
                      data-mol-id="public-build-draft-text"
                    >
                      {reviewText}
                    </pre>
                  )}
                </div>
              )}
              <details>
                <summary>
                  {t(
                    'publicBuild.excluded',
                    { count: draft.excluded.length },
                    { defaultValue: 'Excluded files ({{count}})' },
                  )}
                </summary>
                <ul style={{ maxHeight: 160, overflowY: 'auto', paddingLeft: 18 }}>
                  {draft.excluded.map((file) => (
                    <li key={file.path} style={{ overflowWrap: 'anywhere' }}>
                      <code>{file.path}</code> ·{' '}
                      {t(`share.exclusion.${file.reason}`, undefined, {
                        defaultValue: file.reason,
                      })}
                    </li>
                  ))}
                </ul>
              </details>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 8,
                  margin: '12px 0',
                  lineHeight: 1.5,
                }}
              >
                <input
                  type="checkbox"
                  checked={acknowledged}
                  onChange={(e) => setAcknowledged(e.target.checked)}
                  data-mol-id="public-build-confirm"
                />
                {t('publicBuild.confirm', undefined, {
                  defaultValue:
                    'I reviewed these files and understand anyone with a share link can read, clone and keep this repository.',
                })}
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button
                  color="primary"
                  size="sm"
                  disabled={!acknowledged || busy !== null}
                  onClick={() => void publish()}
                  data-mol-id="public-build-publish"
                >
                  {busy === 'publish'
                    ? t('publicBuild.publishing', undefined, { defaultValue: 'Publishing…' })
                    : t('publicBuild.publish', undefined, {
                        defaultValue: 'Publish reviewed repository',
                      })}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={busy !== null}
                  onClick={() => setDraft(null)}
                >
                  {t('common.cancel', undefined, { defaultValue: 'Cancel' })}
                </Button>
              </div>
            </div>
          )}
        </section>
      )}
      <section>
        <h3 className={cm.fontWeight('bold')}>
          {t('publicBuild.conversations', undefined, { defaultValue: 'Public conversations' })}
        </h3>
        <p className={cm.cn(cm.textMuted, cm.textSize('sm'))}>
          {t('publicBuild.conversationHint', undefined, {
            defaultValue:
              'Conversations are private unless you select them here and save. Selected conversations include future messages and file excerpts, with detected secrets redacted. Review their contents before sharing. Other conversations stay private; the default opens first.',
          })}
        </p>
        {!loaded && !error && (
          <p role="status">{t('common.loading', undefined, { defaultValue: 'Loading…' })}</p>
        )}
        {loaded && sessions.length === 0 && (
          <p className={cm.textMuted}>
            {t('publicBuild.noConversations', undefined, {
              defaultValue: 'No conversations yet. You can import a chat history below.',
            })}
          </p>
        )}
        <ul
          style={{ listStyle: 'none', padding: 0, display: 'grid', gap: 10 }}
          data-mol-id="public-build-sessions"
        >
          {sessions.map((session) => (
            <li
              key={session.id}
              style={{ display: 'flex', alignItems: 'center', gap: 8 }}
              data-mol-id="public-build-session"
            >
              <label
                style={{
                  flex: 1,
                  minWidth: 0,
                  overflowWrap: 'anywhere',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 8,
                }}
              >
                {canAdmin && (
                  <input
                    type="checkbox"
                    checked={selected.includes(session.id)}
                    disabled={busy !== null}
                    onChange={(e) => toggleConversation(session.id, e.target.checked)}
                    data-mol-id="public-build-session-public"
                  />
                )}
                <span>
                  <strong>{session.title}</strong>
                  <span className={cm.textMuted} style={{ display: 'block', fontSize: 12 }}>
                    {session.harness} ·{' '}
                    {t(
                      'publicBuild.turns',
                      { count: session.turns },
                      { defaultValue: '{{count}} turns' },
                    )}
                  </span>
                </span>
              </label>
              {canAdmin && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setReviewConversationId(session.id)}
                  aria-label={t(
                    'publicBuild.reviewConversation',
                    { title: session.title },
                    { defaultValue: 'Review {{title}}' },
                  )}
                  data-mol-id="public-build-review-conversation"
                >
                  {t('publicBuild.review', undefined, { defaultValue: 'Review' })}
                </Button>
              )}
              {canEdit && session.source === 'import' && (
                <Button
                  size="sm"
                  variant="ghost"
                  color="error"
                  disabled={busy !== null}
                  onClick={() => void remove(session.id)}
                  data-mol-id="public-build-remove"
                >
                  {t('publicBuild.removeShort', undefined, { defaultValue: 'Remove' })}
                </Button>
              )}
            </li>
          ))}
        </ul>
        {canAdmin && reviewConversationId && (
          <div
            className={cm.cn(cm.surface, cm.borderAll, cm.sp('p', 3))}
            style={{ maxHeight: 400, overflow: 'auto', borderRadius: 8 }}
            data-mol-id="public-build-conversation-review"
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
              <strong>
                {sessions.find((session) => session.id === reviewConversationId)?.title}
              </strong>
              <Button size="sm" variant="ghost" onClick={() => setReviewConversationId(null)}>
                {t('common.close', undefined, { defaultValue: 'Close' })}
              </Button>
            </div>
            {conversationReviewError ? (
              <p className={cm.textError} role="alert">
                {t('publicBuild.conversationReviewError', undefined, {
                  defaultValue:
                    'This conversation could not be loaded. No other conversation has been substituted.',
                })}
              </p>
            ) : conversationReview ? (
              <SessionReader key={reviewConversationId} session={conversationReview} />
            ) : (
              <p role="status">{t('common.loading', undefined, { defaultValue: 'Loading…' })}</p>
            )}
          </div>
        )}
        {canAdmin && loaded && (
          <>
            <label style={{ display: 'grid', gap: 6 }}>
              {t('publicBuild.defaultConversation', undefined, {
                defaultValue: 'Conversation shown by default',
              })}
              <select
                value={defaultId ?? ''}
                disabled={selected.length === 0 || busy !== null}
                onChange={(e) => setDefaultId(e.target.value || null)}
                className={cm.input()}
                data-mol-id="public-build-default-conversation"
              >
                {selected.length === 0 && (
                  <option value="">
                    {t('publicBuild.noneSelected', undefined, {
                      defaultValue: 'No public conversations',
                    })}
                  </option>
                )}
                {sessions
                  .filter((s) => selected.includes(s.id))
                  .map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.title}
                    </option>
                  ))}
              </select>
            </label>
            <Button
              size="sm"
              variant="outline"
              disabled={busy !== null}
              onClick={() => void saveConversations()}
              data-mol-id="public-build-save-conversations"
            >
              {t('publicBuild.saveConversations', undefined, {
                defaultValue: 'Save conversation sharing',
              })}
            </Button>
          </>
        )}
      </section>
      {canEdit && (
        <div
          className={cm.cn(cm.surface, cm.borderAll, cm.sp('p', 3))}
          style={{ borderRadius: 8, borderStyle: dragging ? 'solid' : 'dashed' }}
          onDragOver={(e) => {
            e.preventDefault()
            setDragging(true)
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault()
            setDragging(false)
            void importFiles(Array.from(e.dataTransfer.files))
          }}
          data-mol-id="public-build-dropzone"
        >
          <p>
            {t('publicBuild.importLede', undefined, {
              defaultValue:
                'Import chat histories from another coding agent. Imported conversations stay private until you select and save them above.',
            })}
          </p>
          <input
            ref={inputRef}
            type="file"
            multiple
            hidden
            accept={IMPORT_ACCEPT}
            onChange={(e) => {
              const files = Array.from(e.target.files ?? [])
              e.target.value = ''
              void importFiles(files)
            }}
            data-mol-id="public-build-file-input"
          />
          <Button size="sm" variant="outline" onClick={() => inputRef.current?.click()}>
            {t('publicBuild.choose', undefined, { defaultValue: 'Choose files' })}
          </Button>
        </div>
      )}
      {rows.length > 0 && (
        <ul data-mol-id="public-build-results">
          {rows.map((row, index) => (
            <li
              key={`${row.name}-${index}`}
              data-mol-id="public-build-result"
              data-status={row.status}
            >
              <strong>{row.name}</strong> {statusText(row)}
            </li>
          ))}
        </ul>
      )}
      {notice && (
        <p role="status" data-mol-id="public-build-notice">
          {notice}
        </p>
      )}
      {error && (
        <div role="alert">
          <p className={cm.textError} data-mol-id="public-build-error">
            {error}
          </p>
          {!loaded && (
            <Button
              onClick={() => {
                setError(null)
                void loadRef.current()
              }}
            >
              {t('common.retry', undefined, { defaultValue: 'Try again' })}
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

/** Owner section injected into all Share dialog entry points. */
export function PublicBuildShareSection({
  projectId,
  canAdmin,
  showDivider,
}: {
  projectId: string
  canAdmin: boolean
  showDivider: boolean
}) {
  return (
    <div
      data-mol-id="share-public-build"
      style={
        showDivider
          ? { borderTop: '1px solid var(--color-border)', marginTop: 16, paddingTop: 12 }
          : undefined
      }
    >
      <PublicBuildPanel projectId={projectId} canAdmin={canAdmin} canEdit />
    </div>
  )
}
